import { createFileRoute } from "@tanstack/react-router"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { toast } from "react-hot-toast"

import MapContent from "@/components/coverage-map/MapContent"
import SearchControls from "@/components/coverage-map/SearchControls"
import {
  type CoverageMode,
  DEFAULT_COVERAGE_MODE,
  DEFAULT_COVERAGE_RADIUS_KM,
  DEFAULT_INITIAL_CENTER,
  DEFAULT_INITIAL_ZOOM,
  DEFAULT_IS_METRIC,
  DEFAULT_MAP_TYPE,
  DEFAULT_NEAREST_STATIONS_COUNT,
  DEFAULT_SHOW_COVERAGE,
  FLY_DURATION,
  FLY_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
} from "@/components/coverage-map/constants"
import { useFeatures, useStations } from "@/hooks"
import { type Station, isNxStation } from "@/services/types"
import { haversineDistance } from "@/utils/haversine"

const ADAPTIVE_ZOOM_BASE_HEIGHT_PX = 800
const ADAPTIVE_ZOOM_MAX = 1.35

export function findNearestNxStations(
  stations: Array<Station>,
  lat: number,
  lon: number,
  count: number,
): Array<{ dist: number; index: number; healthy: boolean }> {
  return stations
    .flatMap((station, index) =>
      isNxStation(station)
        ? [
            {
              dist: haversineDistance(station.lat, station.lon, lat, lon),
              index,
              healthy: station.healthy,
            },
          ]
        : [],
    )
    .sort((a, b) => a.dist - b.dist)
    .slice(0, count)
}

function getAdaptiveInitialZoom(): number {
  if (typeof window === "undefined") {
    return DEFAULT_INITIAL_ZOOM
  }

  const adaptiveZoom =
    DEFAULT_INITIAL_ZOOM +
    Math.log2(window.innerHeight / ADAPTIVE_ZOOM_BASE_HEIGHT_PX)
  return Math.min(
    ADAPTIVE_ZOOM_MAX,
    Math.max(DEFAULT_INITIAL_ZOOM, Math.round(adaptiveZoom * 100) / 100),
  )
}

// Stable identity for the pre-first-load case: the deck.gl layer memos below
// key on `stations`, and a fresh [] each render would rebuild them every time.
const EMPTY_STATIONS: Array<Station> = []

export const Route = createFileRoute("/_auth/coverage-map/")({
  component: () => <RTKMap />,
  staticData: {
    title: "Skylark Nx RTK Coverage Map",
  },
})

// Exported for tests: the route's component is not otherwise reachable without
// standing up a router.
export const RTKMap: React.FC = () => {
  const initialZoom = getAdaptiveInitialZoom()
  const mapContainerRef = useRef<HTMLDivElement>(null)
  // Polled on an interval by useStations, so a station batch written by the
  // 5-minute station-health worker reaches an already-open map without a page
  // reload. A refetch (failed or in flight) keeps the previous value, so the
  // layers below re-render from fresh data rather than blanking in between.
  const {
    data: stationData,
    isPending: loading,
    isError: stationsFailed,
  } = useStations()
  const stations = stationData?.stations ?? EMPTY_STATIONS
  const stationsGatheredAt = stationData?.gatheredAt ?? null
  const [searchLocation, setSearchLocation] = useState<{
    lat: number
    lon: number
  } | null>(null)
  const [address, setAddress] = useState("")
  const [pinnedAddress, setPinnedAddress] = useState<string | null>(null)
  const [isMetric, setIsMetric] = useState(DEFAULT_IS_METRIC)
  const [nearestStations, setNearestStations] = useState<
    Array<{ dist: number; index: number; healthy: boolean }>
  >([])
  const [showCoverage, setShowCoverage] = useState(DEFAULT_SHOW_COVERAGE)
  const [resetKey, setResetKey] = useState(0)
  const [coverageRadius, setCoverageRadius] = useState(
    DEFAULT_COVERAGE_RADIUS_KM,
  )
  const [nearestStationsCount, setNearestStationsCount] = useState(
    DEFAULT_NEAREST_STATIONS_COUNT,
  )
  const [mapType, setMapType] = useState<"light" | "dark">(DEFAULT_MAP_TYPE)
  const [coverageMode, setCoverageMode] = useState<CoverageMode>(
    DEFAULT_COVERAGE_MODE,
  )
  // SQI is gated on the backend FEATURE_SQI flag, discovered at runtime.
  // Default to disabled until the flag loads so we never offer SQI (and issue
  // grid fetches that would 404) against a backend where it's off.
  const { data: features } = useFeatures()
  const sqiEnabled = features?.sqi ?? false
  const sqiDebugAllowed = features?.sqiDebugAllowed ?? false
  const [debugMode, setDebugMode] = useState(false)

  // If SQI is (or becomes) disabled on the backend, fall back to baseline so
  // we never render the SQI layer or issue grid fetches against a backend
  // where the feature is off.
  useEffect(() => {
    if (!sqiEnabled && coverageMode === "sqi") {
      setCoverageMode("baseline")
    }
  }, [sqiEnabled, coverageMode])

  useEffect(() => {
    if (!sqiDebugAllowed && debugMode) {
      setDebugMode(false)
    }
  }, [sqiDebugAllowed, debugMode])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [flyToLocation, setFlyToLocation] = useState<{
    lat: number
    lon: number
    zoom: number
    id: number
  } | null>(null)

  // Toast on the transition into failure, not on every failed attempt:
  // useStations retries on its own backoff, and one toast per retry would turn
  // a backend blip into a wall of them.
  //
  // The two failures are not the same event. With no data yet the map is empty
  // and the user has nothing to look at; once a batch has loaded, a failed poll
  // leaves the previous stations on screen and is only a staleness note.
  const wasStationsFailed = useRef(false)
  useEffect(() => {
    if (stationsFailed && !wasStationsFailed.current) {
      toast.error(
        stationData
          ? "Unable to refresh coverage map data. Showing the last known data and retrying."
          : "Unable to load coverage map data. Please refresh or try again later.",
      )
    }
    wasStationsFailed.current = stationsFailed
  }, [stationsFailed, stationData])

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
  }, [])

  const updateNearest = useCallback(
    (lat: number, lon: number) => {
      setSearchLocation({ lat, lon })
      if (stations.length === 0) return

      // Top N closest NX stations regardless of health — shown in the selector
      setNearestStations(
        findNearestNxStations(stations, lat, lon, nearestStationsCount),
      )
    },
    [stations, nearestStationsCount],
  )

  useEffect(() => {
    if (searchLocation) {
      updateNearest(searchLocation.lat, searchLocation.lon)
    }
  }, [nearestStationsCount, updateNearest, searchLocation])

  const handleMapClick = (lat: number, lon: number) => {
    const coords = `${lat.toFixed(6)}, ${lon.toFixed(6)}`
    setAddress(coords)
    setPinnedAddress(coords)
    updateNearest(lat, lon)
  }

  const handleSearchSelect = useCallback(
    (lat: number, lon: number) => {
      updateNearest(lat, lon)
      setFlyToLocation({ lat, lon, zoom: FLY_ZOOM, id: Date.now() })
    },
    [updateNearest],
  )

  const handleReset = () => {
    setSearchLocation(null)
    setAddress("")
    setPinnedAddress(null)
    setNearestStations([])
    setShowCoverage(DEFAULT_SHOW_COVERAGE)
    setIsMetric(DEFAULT_IS_METRIC)
    setCoverageRadius(DEFAULT_COVERAGE_RADIUS_KM)
    setNearestStationsCount(DEFAULT_NEAREST_STATIONS_COUNT)
    setMapType(DEFAULT_MAP_TYPE)
    setCoverageMode(DEFAULT_COVERAGE_MODE)
    setFlyToLocation(null)
    setResetKey((prev) => prev + 1)
  }

  return (
    <>
      <div
        ref={mapContainerRef}
        className={`bg-white overflow-hidden ${
          isFullscreen
            ? "fixed inset-0"
            : "fixed top-20 left-0 md:left-64 right-0 bottom-[64px]"
        }`}
      >
        <MapContent
          key={resetKey}
          stations={stations}
          searchLocation={searchLocation}
          nearestStations={nearestStations}
          onMapClick={handleMapClick}
          loading={loading}
          isMetric={isMetric}
          showCoverage={showCoverage}
          initialCenter={DEFAULT_INITIAL_CENTER}
          initialZoom={initialZoom}
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          coverageRadius={coverageRadius}
          mapType={mapType}
          flyToLocation={flyToLocation}
          flyDuration={FLY_DURATION}
          containerRef={mapContainerRef}
          coverageMode={coverageMode}
          debugMode={debugMode && sqiDebugAllowed}
          stationsGatheredAt={stationsGatheredAt}
        />

        <SearchControls
          address={address}
          setAddress={setAddress}
          pinnedAddress={pinnedAddress}
          setPinnedAddress={setPinnedAddress}
          onSelectCoords={handleSearchSelect}
          isMetric={isMetric}
          setIsMetric={setIsMetric}
          showCoverage={showCoverage}
          setShowCoverage={setShowCoverage}
          onReset={handleReset}
          searchLocation={searchLocation}
          coverageRadius={coverageRadius}
          setCoverageRadius={setCoverageRadius}
          nearestStationsCount={nearestStationsCount}
          setNearestStationsCount={setNearestStationsCount}
          mapType={mapType}
          setMapType={setMapType}
          coverageMode={coverageMode}
          setCoverageMode={setCoverageMode}
          sqiEnabled={sqiEnabled}
          sqiDebugAllowed={sqiDebugAllowed}
          debugMode={debugMode}
          setDebugMode={setDebugMode}
        />
      </div>

      {/* Footer - separate container below the map */}
      {!isFullscreen && (
        <div className="fixed bottom-0 left-0 md:left-64 right-0 h-[64px] z-900 bg-white/80 backdrop-blur-sm border-t border-gray-200">
          <div className="px-6 py-3 text-xs text-gray-500 leading-relaxed">
            <p>
              Coverage and performance depend on factors such as receiver and
              antenna characteristics, environment and use-case dynamics.
            </p>
            <p>
              For coverage in Australia, China, and India, please{" "}
              <a
                href="https://www.swiftnav.com/contact-us"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-gray-700"
              >
                contact us
              </a>
              .
            </p>
          </div>
        </div>
      )}
    </>
  )
}
